import { getSbUrl, hasFullLiveAccess, hasRight, hasOrganiserRights, hasParentMeeting } from "@helpers"

import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  CalendarDaysIcon,
  ChartPieIcon,
  CircleStackIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CursorArrowRippleIcon,
  DocumentDuplicateIcon,
  HandThumbUpIcon,
  PresentationChartLineIcon,
  QuestionMarkCircleIcon,
  SquaresPlusIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline"

const getEventLinks = (eventId, user) => {
  const links = [
    {
      caption: "Create New Event",
      expandable: false,
      href: hasFullLiveAccess() ? "#" : "/coming-soon",
      target: "_self",
      icon: <SquaresPlusIcon />,
      id: "CREATE_EVENT",
      subMenu: null,
      type: "TOP_LEVEL_LINK",
      isAdmin: true,
      updateActiveLinkOnClick: true,
    }
  ]

  if (user?.me?.defaultEvent !== null) {
    links.push({
      caption: "Switch Event",
      expandable: false,
      href: "#",
      target: "_self",
      icon: <ArrowPathIcon />,
      id: "SWITCH_EVENT",
      subMenu: null,
      type: "TOP_LEVEL_LINK",
      isAdmin: false,
      updateActiveLinkOnClick: false,
    })
  }

  return links
}

const getCurrentEventLinks = (eventId, user) => {
  const showMySchedule = hasParentMeeting(user)
  const showReports = hasOrganiserRights(user) || (!hasOrganiserRights(user) && user?.me?.client?.settings?.allow_teacher_reports) 

  const dashboardLinks = [
    {
      caption: "Event Dashboard",
      expandable: false,
      href: `/event/${eventId}/dashboard`,
      target: "_self",
      icon: <PresentationChartLineIcon />,
      id: "EVENT_DASHBOARD",
      subMenu: null,
      type: "TOP_LEVEL_LINK",
      isAdmin: true,
      updateActiveLinkOnClick: true,
    }
  ]

  const manageEventLinks = [
    {
      caption: "Manage Event",
      expandable: true,
      href: `#`,
      icon: <CalendarDaysIcon />,
      id: "EVENT_MANAGE",
      type: "TOP_LEVEL_LINK",
      isAdmin: true,
      subMenu: [
        {
          caption: "Groups",
          expandable: false,
          href: hasFullLiveAccess() ? `/event/${eventId}/groups` : "/coming-soon",
          target: "_self",
          isAdmin: true,
          id: "EVENT_MANAGE_GROUPS",
          icon: null,
          subMenu: null,
          type: "SUB_MENU_LINK",
        },
        {
          caption: "Invitations",
          expandable: false,
          href: hasFullLiveAccess() ? `/event/${eventId}/invitations` : "/coming-soon",
          target: "_self",
          isAdmin: true,
          id: "EVENT_MANAGE_INVITATIONS",
          icon: null,
          subMenu: null,
          type: "SUB_MENU_LINK",
        },
        {
          caption: "Communications",
          expandable: false,
          href:  hasFullLiveAccess() ? `/event/${eventId}/communications` : "/coming-soon",
          target: "_self",
          isAdmin: true,
          id: "EVENT_MANAGE_EMAILS",
          icon: null,
          subMenu: null,
          type: "SUB_MENU_LINK",
        },
        {
          caption: "Bookings",
          expandable: false,
          href: hasFullLiveAccess() ? `/event/${eventId}/bookings` : "/coming-soon",
          target: "_self",
          isAdmin: true,
          id: "EVENT_MANAGE_BOOKINGS",
          icon: null,
          subMenu: null,
          type: "SUB_MENU_LINK",
        },
        {
          caption: "Overview",
          expandable: false,
          href:  hasFullLiveAccess() ? `/event/${eventId}/overview` : "/coming-soon",
          target: "_self",
          icon: null,
          id: "EVENT_MANAGE_OVERVIEW",
          subMenu: null,
          type: "SUB_MENU_LINK",
          isAdmin: true,
        },
        {
          caption: "Audit Log",
          expandable: false,
          href: `/event/${eventId}/audit-log`,
          target: "_self",
          id: "EVENT_AUDIT_LOG",
          isAdmin: true,
          icon: null,
          subMenu: null,
          type: "SUB_MENU_LINK",
        },
      ]
    }
  ]

  const reportLinks = [
    {
      caption: "Event Reports",
      expandable: true,
      href: "#",
      icon: <ChartPieIcon />,
      id: "EVENT_REPORTS",
      subMenu: [
        {
          caption: "Event Attendance",
          expandable: false,
          href: `/event/${eventId}/reports/attendance`,
          target: "_self",
          id: "EVENT_REPORTS_ATTENDANCE",
          icon: null,
          subMenu: null,
          type: "SUB_MENU_LINK",
        },
        {
          caption: "Event Comments",
          expandable: false,
          href: `/event/${eventId}/reports/comments`,
          target: "_self",
          id: "EVENT_REPORTS_COMMENTS",
          icon: null,
          subMenu: null,
          type: "SUB_MENU_LINK",
        },
        {
          caption: "Parent Bookings & Itinerary",
          expandable: false,
          href: `/event/${eventId}/reports/parent-bookings`,
          target: "_self",
          id: "EVENT_REPORTS_PARENT_BOOKINGS",
          icon: null,
          subMenu: null,
          type: "SUB_MENU_LINK",
        },
        {
          caption: "Teacher Bookings & Itinerary",
          expandable: false,
          href: `/event/${eventId}/reports/teacher-bookings`,
          target: "_self",
          id: "EVENT_REPORTS_TEACHER_BOOKINGS",
          icon: null,
          subMenu: null,
          type: "SUB_MENU_LINK",
        },
      ],
      type: "TOP_LEVEL_LINK",
      isAdmin: false,
      updateActiveLinkOnClick: false,
    },
  ]

  const yourScheduleLinks = [
    {
      caption: "Your Schedule",
      expandable: false,
      href: `/event/${eventId}/schedule`,
      target: "_self",
      icon: <ClipboardDocumentListIcon />,
      id: "STAFF_SCHEDULE",
      type: "TOP_LEVEL_LINK",
    }
  ]

  return [
    ...dashboardLinks,
    ...(showMySchedule ? yourScheduleLinks : []),
    ...(showReports ? reportLinks : []),
    ...manageEventLinks,
  ]
}

const getSiteLinks = () => [
  {
    caption: "Data Imports",
    expandable: false,
    href: `/data/imports`,
    target: "_self",
    icon: <TableCellsIcon />,
    id: "SITE_DATA_IMPORTS",
    subMenu: null,
    type: "TOP_LEVEL_LINK",
    isAdmin: true,
  },
  {
    caption: "Site Data",
    expandable: true,
    href: "#",
    icon: <CircleStackIcon />,
    id: "SITE_DATA",
    subMenu: [
      {
        caption: "Guardians",
        expandable: false,
        href: "/data/guardians",
        target: "_self",
        id: "SITE_DATA_GUARDIANS",
        icon: null,
        subMenu: null,
        type: "SUB_MENU_LINK",
      },
      {
        caption: "Lessons",
        expandable: false,
        href: "/data/lessons",
        target: "_self",
        id: "SITE_DATA_LESSONS",
        icon: null,
        subMenu: null,
        type: "SUB_MENU_LINK",
      },
      {
        caption: "Staff",
        expandable: false,
        href: "/data/staff",
        target: "_self",
        id: "SITE_DATA_STAFF",
        icon: null,
        subMenu: null,
        type: "SUB_MENU_LINK",
      },
      {
        caption: "Students",
        expandable: false,
        href: "/data/students",
        target: "_self",
        id: "SITE_DATA_STUDENTS",
        icon: null,
        subMenu: null,
        type: "SUB_MENU_LINK",
      },
      {
        caption: "Subjects",
        expandable: false,
        href: "/data/subjects",
        target: "_self",
        id: "SITE_DATA_SUBJECTS",
        icon: null,
        subMenu: null,
        type: "SUB_MENU_LINK",
      },
    ],
    type: "TOP_LEVEL_LINK",
    isAdmin: true,
    updateActiveLinkOnClick: false,
  },
  {
    caption: "Site Settings",
    expandable: false,
    href: `/settings`,
    target: "_self",
    icon: <Cog6ToothIcon />,
    id: "SITE_SETTINGS",
    subMenu: null,
    type: "TOP_LEVEL_LINK",
    isAdmin: true,
  },
]

const getSupportLinks = () => [
  {
    caption: "Help",
    expandable: false,
    href: hasRight("admin") ? getSbUrl("/apps/administration/support.php") : "https://help.schoolbooking.com/category/parent-meetings/parent-meeting-articles-for-teachers",
    target: hasRight("admin") ? "_self" : "_blank",
    icon: <QuestionMarkCircleIcon />,
    id: "SUPPORT_HELP",
    subMenu: null,
    type: "TOP_LEVEL_LINK",
    isAdmin: false,
    updateActiveLinkOnClick: false,
  },
  {
    caption: "Your Feedback",
    expandable: false,
    href: "/feedback",
    target: "_self",
    icon: <HandThumbUpIcon />,
    id: "SUPPORT_FEEDBACK",
    subMenu: null,
    type: "TOP_LEVEL_LINK",
    isAdmin: false,
  },
  {
    caption: "Change Request",
    expandable: false,
    href: "https://forms.office.com/r/q46Dn6uVK4",
    target: "_blank",
    icon: <DocumentDuplicateIcon />,
    id: "SUPPORT_CHANGE_REQUEST",
    subMenu: null,
    type: "TOP_LEVEL_LINK",
    isAdmin: false,
  },
  {
    caption: "Revert to Old Version",
    expandable: false,
    href: null,
    target: "_blank",
    icon: <ArrowsRightLeftIcon />,
    id: "SUPPORT_OLD_VERSION",
    subMenu: null,
    type: "TOP_LEVEL_LINK",
    isAdmin: false,
    updateActiveLinkOnClick: false,
  },
  {
    caption: "Product Tour",
    expandable: false,
    href: "https://www.guidejar.com/embed/9cc67aae-f4d3-4119-9398-cb6b4833f031?type=1&controls=on",
    target: "_blank",
    icon: <CursorArrowRippleIcon />,
    id: "PRODUCT_TOUR",
    subMenu: null,
    type: "TOP_LEVEL_LINK",
    isAdmin: true,
  }
]

const getLinks = (eventId = null, user) => {
  const links = []

  if (hasOrganiserRights(user) || user?.me?.defaultEvent !== null) {
    links.push({ id: "EVENT_OPTIONS", caption: "Event Options", links: getEventLinks(eventId, user), isAdminOnly: false })
  }

  if (user?.me?.defaultEvent !== null) {
    links.push({ id: "CURRENT_EVENT_OPTIONS", caption: "Current Event Options", links: getCurrentEventLinks(eventId, user), isAdminOnly: false })
  }

  links.push(
    { id: "SITE_OPTIONS", caption: "Site Options", links: getSiteLinks(), isAdminOnly: true },
    { id: "SUPPORT_OPTIONS", caption: "Support", links: getSupportLinks(), isAdminOnly: false },
  )

  return links
}

export default getLinks
